"use client";

import { useState } from "react";
import {
  Users,
  Warehouse,
  GraduationCap,
  Share2,
  CheckCircle2,
  ArrowRight,
  Building2,
  Globe,
  Shield,
  TrendingUp,
  Handshake,
  Clock,
  BarChart3,
  Package,
  Target,
  Award,
  Phone,
  Mail,
  MessageCircle,
} from "lucide-react";

const partnerTypes = [
  {
    icon: Warehouse,
    title: "Regional Distributor",
    subtitle: "Exclusive regional distribution rights",
    gradient: "from-emerald-500/20 to-teal-500/20",
    borderGradient: "from-emerald-500/40 to-teal-500/40",
    highlights: [
      "Exclusive territory protection",
      "Volume-based tiered pricing",
      "Marketing co-funding support",
      "Dedicated account manager",
      "Sales training & enablement",
      "First access to new products",
    ],
    requirement: "Min. 5 years in auto parts + warehousing capacity",
    target: "Established auto parts distributors",
  },
  {
    icon: Users,
    title: "Certified Repair Partner",
    subtitle: "Authorized EV repair service centers",
    gradient: "from-cyan-500/20 to-blue-500/20",
    borderGradient: "from-cyan-500/40 to-blue-500/40",
    highlights: [
      "Certified technician training",
      "Official tooling & equipment supply",
      "Technical support hotline 24/7",
      "Warranty parts at preferential rates",
      "Customer referral program",
      "Listed on our partner directory",
    ],
    requirement: "Existing repair shop + certified techs",
    target: "Repair shops & garages",
  },
  {
    icon: GraduationCap,
    title: "Training Partner",
    subtitle: "Co-branded training institutions",
    gradient: "from-violet-500/20 to-fuchsia-500/20",
    borderGradient: "from-violet-500/40 to-fuchsia-500/40",
    highlights: [
      "Licensed curriculum & materials",
      "Train-the-trainer program",
      "Equipment package discounts",
      "Joint certification issuing",
      "Student placement network",
      "Revenue share model",
    ],
    requirement: "Vocational schools & training centers",
    target: "Educational institutions",
  },
  {
    icon: Share2,
    title: "Referral Partner",
    subtitle: "Affiliate & referral program",
    gradient: "from-amber-500/20 to-orange-500/20",
    borderGradient: "from-amber-500/40 to-orange-500/40",
    highlights: [
      "Generous commission structure",
      "No inventory required",
      "Personalized referral link",
      "Real-time tracking dashboard",
      "Marketing materials provided",
      "Monthly payouts",
    ],
    requirement: "Industry connections or audience",
    target: "Consultants / KOLs / influencers",
  },
];

const benefits = [
  {
    icon: TrendingUp,
    title: "Revenue Growth",
    description: "Tap into the $250B+ global EV aftermarket with a proven business model",
  },
  {
    icon: Shield,
    title: "Brand Credibility",
    description: "Partner with a leading Chinese EV after-sales technology company",
  },
  {
    icon: Building2,
    title: "Full Support",
    description: "Technical, marketing, training, and operations support from day one",
  },
  {
    icon: Globe,
    title: "Global Supply Chain",
    description: "Access to 2,000+ SKUs of quality NEV parts directly from factories",
  },
];

const processSteps = [
  { step: "01", title: "Submit Application", desc: "Fill out the form below — takes 2 minutes" },
  { step: "02", title: "Initial Review", desc: "We review your profile within 3 business days" },
  { step: "03", title: "Intro Call", desc: "Video call to discuss fit, terms, and expectations" },
  { step: "04", title: "Agreement & Onboarding", desc: "Sign agreement and start the 30-day onboarding program" },
  { step: "05", title: "Grow Together", desc: "Hit milestones, unlock benefits, scale your business" },
];

const stats = [
  { value: "30+", label: "Partner Countries" },
  { value: "200+", label: "Active Partners" },
  { value: "$50M+", label: "Partner Revenue" },
  { value: "95%", label: "Renewal Rate" },
];

export default function PartnersPage() {
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    partner_type: "distributor",
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    whatsapp: "",
    country: "",
    city: "",
    website: "",
    industry: "",
    years_in_business: "",
    team_size: "",
    why_partner: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch("/api/partner-apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-primary)]/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[var(--color-primary)]/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-6xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm font-medium mb-6">
            <Handshake className="w-4 h-4" />
            Partner Program
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Grow Your Business with{" "}
            <span className="bg-gradient-to-r from-[var(--color-primary)] via-[var(--color-accent)] to-[var(--color-primary)] bg-clip-text text-transparent">
              WFD New Energy
            </span>
          </h1>
          <p className="text-lg md:text-xl text-[var(--color-muted)] max-w-2xl mx-auto mb-10 leading-relaxed">
            Join our global partner network and tap into the booming EV aftermarket. 
            Get exclusive access to technology, training, parts, and marketing support — 
            backed by 10+ years of industry expertise.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-12">
            {stats.map((s, i) => (
              <div key={i} className="p-5 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="text-sm text-[var(--color-muted)] mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <a
            href="#apply"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-[var(--color-primary)] text-[var(--color-background)] font-semibold hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all hover:-translate-y-0.5"
          >
            Apply to Become a Partner
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Partner With Us?</h2>
            <p className="text-[var(--color-muted)] max-w-2xl mx-auto">
              We don&apos;t just sell products — we build long-term partnerships 
              with shared goals and mutual growth.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/50 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <b.icon className="w-6 h-6 text-[var(--color-primary)]" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{b.title}</h3>
                <p className="text-sm text-[var(--color-muted)] leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 px-6 bg-[var(--color-surface)]/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Path</h2>
            <p className="text-[var(--color-muted)] max-w-2xl mx-auto">
              Four partnership models designed for different business types and goals.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {partnerTypes.map((p, i) => (
              <div
                key={i}
                className="group relative p-8 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-transparent transition-all duration-500 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${p.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${p.borderGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none p-[1px]`}>
                  <div className="w-full h-full bg-[var(--color-surface)] rounded-2xl" />
                </div>

                <div className="relative">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${p.gradient} border border-white/10 flex items-center justify-center mb-5`}>
                    <p.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">{p.title}</h3>
                  <p className="text-[var(--color-muted)] text-sm mb-5">{p.subtitle}</p>

                  <ul className="space-y-2.5 mb-6">
                    {p.highlights.map((h, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[var(--color-primary)] shrink-0 mt-0.5" />
                        <span className="text-[var(--color-foreground)]/80">{h}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-5 border-t border-[var(--color-border)] space-y-2">
                    <div className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
                      <Target className="w-3.5 h-3.5" />
                      <span>Ideal for: <span className="text-[var(--color-foreground)]/70">{p.target}</span></span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
                      <Award className="w-3.5 h-3.5" />
                      <span>Requirement: <span className="text-[var(--color-foreground)]/70">{p.requirement}</span></span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-[var(--color-muted)] max-w-2xl mx-auto">
              A simple 5-step onboarding process — from application to first revenue.
            </p>
          </div>
          <div className="relative">
            <div className="absolute top-10 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--color-primary)]/30 to-transparent hidden md:block" />
            <div className="grid md:grid-cols-5 gap-6">
              {processSteps.map((s, i) => (
                <div key={i} className="text-center relative">
                  <div className="w-20 h-20 mx-auto rounded-full bg-[var(--color-surface)] border-2 border-[var(--color-primary)]/30 flex items-center justify-center mb-4 relative z-10 group-hover:scale-110 transition-all">
                    <span className="text-2xl font-bold bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="font-semibold mb-1.5">{s.title}</h3>
                  <p className="text-sm text-[var(--color-muted)]">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Apply Form */}
      <section id="apply" className="py-20 px-6 bg-[var(--color-surface)]/50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Apply Now</h2>
            <p className="text-[var(--color-muted)]">
              {submitted
                ? "Thanks! We'll review your application within 3 business days."
                : "Fill out the form — our partner team will reach out within 3 business days."}
            </p>
          </div>

          {submitted ? (
            <div className="p-8 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-primary)]/30 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[var(--color-primary)]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Application Submitted!</h3>
              <p className="text-[var(--color-muted)]">
                Thank you for your interest in the WFD Partner Program. 
                Our partnerships team will review your application and contact you via email within 3 business days.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="p-8 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)] space-y-5"
            >
              {/* Step 1: Partner Type */}
              {formStep === 1 && (
                <>
                  <label className="block">
                    <span className="block text-sm font-medium mb-2">I&apos;m interested in</span>
                    <select
                      value={formData.partner_type}
                      onChange={(e) => setFormData({ ...formData, partner_type: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                    >
                      <option value="distributor">Regional Distributor</option>
                      <option value="dealer">Certified Repair Partner</option>
                      <option value="trainer">Training Partner</option>
                      <option value="affiliate">Referral / Affiliate Partner</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium mb-2">Company Name *</span>
                    <input
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      placeholder="Your company name"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium mb-2">Website (optional)</span>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      placeholder="https://yourcompany.com"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setFormStep(2)}
                    className="w-full py-3.5 rounded-xl bg-[var(--color-primary)] text-[var(--color-background)] font-semibold hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all"
                  >
                    Continue <ArrowRight className="w-4 h-4 inline ml-1" />
                  </button>
                </>
              )}

              {/* Step 2: Contact */}
              {formStep === 2 && (
                <>
                  <label className="block">
                    <span className="block text-sm font-medium mb-2">Contact Name *</span>
                    <input
                      type="text"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      placeholder="Full name"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium mb-2">Email *</span>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      placeholder="you@company.com"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">Phone</span>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                        placeholder="+971..."
                      />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">WhatsApp</span>
                      <input
                        type="tel"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                        placeholder="+971..."
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">Country *</span>
                      <input
                        type="text"
                        required
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                        placeholder="UAE"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">City</span>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                        placeholder="Dubai"
                      />
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setFormStep(1)}
                      className="flex-1 py-3.5 rounded-xl border border-[var(--color-border)] font-medium hover:border-[var(--color-primary)]/50 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormStep(3)}
                      className="flex-1 py-3.5 rounded-xl bg-[var(--color-primary)] text-[var(--color-background)] font-semibold hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all"
                    >
                      Continue <ArrowRight className="w-4 h-4 inline ml-1" />
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Business Info */}
              {formStep === 3 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">Industry</span>
                      <select
                        value={formData.industry}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      >
                        <option value="">Select...</option>
                        <option value="auto_parts">Auto Parts Distribution</option>
                        <option value="repair_shop">Repair Shop / Garage</option>
                        <option value="training">Training / Education</option>
                        <option value="consulting">Consulting / Agency</option>
                        <option value="fleet">Fleet Management</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">Years in Business</span>
                      <select
                        value={formData.years_in_business}
                        onChange={(e) => setFormData({ ...formData, years_in_business: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      >
                        <option value="">Select...</option>
                        <option value="0-2">0-2 years</option>
                        <option value="3-5">3-5 years</option>
                        <option value="5-10">5-10 years</option>
                        <option value="10+">10+ years</option>
                      </select>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">Team Size</span>
                      <select
                        value={formData.team_size}
                        onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      >
                        <option value="">Select...</option>
                        <option value="1-10">1-10 people</option>
                        <option value="11-30">11-30 people</option>
                        <option value="31-100">31-100 people</option>
                        <option value="100+">100+ people</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium mb-2">Monthly Revenue</span>
                      <select
                        value={(formData as any).monthly_revenue || ""}
                        onChange={(e) => setFormData({ ...formData, monthly_revenue: e.target.value } as any)}
                        className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
                      >
                        <option value="">Select...</option>
                        <option value="<10k">Under $10K</option>
                        <option value="10k-50k">$10K - $50K</option>
                        <option value="50k-200k">$50K - $200K</option>
                        <option value="200k+">$200K+</option>
                      </select>
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-sm font-medium mb-2">
                      Why do you want to partner with WFD?
                    </span>
                    <textarea
                      rows={3}
                      value={formData.why_partner}
                      onChange={(e) => setFormData({ ...formData, why_partner: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your business goals and how we can help..."
                    />
                  </label>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setFormStep(2)}
                      className="flex-1 py-3.5 rounded-xl border border-[var(--color-border)] font-medium hover:border-[var(--color-primary)]/50 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] text-[var(--color-background)] font-semibold hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all disabled:opacity-50"
                    >
                      {submitting ? "Submitting..." : "Submit Application"}
                    </button>
                  </div>
                </>
              )}
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
